import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  Feature,
  FeatureGroup,
  FeatureFilters,
  CreateFeatureDTO,
  UpdateFeatureDTO,
  CreateFeatureGroupDTO,
  UpdateFeatureGroupDTO,
  getAllFeatures,
  getFeatureById,
  createFeature,
  updateFeature,
  deleteFeature,
  getAllFeatureGroupsWithFeatures,
  getFeatureGroupById,
  createFeatureGroup,
  updateFeatureGroup,
  deleteFeatureGroup,
  getFeaturesByGroupId,
} from '@/services/Features/features.service';

export const featuresApiSlice = createApi({
  reducerPath: 'featuresApi',
  tagTypes: ['Features', 'Feature', 'FeatureGroups', 'FeatureGroup'],
  baseQuery: fakeBaseQuery(),
  endpoints: (builder) => ({
    // Feature endpoints
    getAllFeatures: builder.query<Feature[], FeatureFilters | undefined>({
      queryFn: async (filters) => {
        try {
          const data = await getAllFeatures(filters);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({
                type: 'Features' as const,
                id: _id,
              })),
              { type: 'Features', id: 'LIST' },
            ]
          : [{ type: 'Features', id: 'LIST' }],
    }),

    getFeatureById: builder.query<Feature, string>({
      queryFn: async (id) => {
        try {
          const data = await getFeatureById(id);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      providesTags: (result) => (result ? [{ type: 'Feature', id: result._id }] : []),
    }),

    getFeaturesByGroupId: builder.query<Feature[], string>({
      queryFn: async (groupId) => {
        try {
          const data = await getFeaturesByGroupId(groupId);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      providesTags: (result, groupId) =>
        result
          ? [
              ...result.map(({ _id }) => ({
                type: 'Features' as const,
                id: _id,
              })),
              { type: 'Features', id: 'GROUP_' + groupId },
            ]
          : [{ type: 'Features', id: 'GROUP_' + groupId }],
    }),

    createFeature: builder.mutation<Feature, CreateFeatureDTO>({
      queryFn: async (featureData) => {
        try {
          const data = await createFeature(featureData);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      invalidatesTags: [{ type: 'Features', id: 'LIST' }],
    }),

    updateFeature: builder.mutation<Feature, { id: string; featureData: UpdateFeatureDTO }>({
      queryFn: async ({ id, featureData }) => {
        try {
          const data = await updateFeature(id, featureData);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'Features', id: 'LIST' },
              { type: 'Feature', id: result._id },
            ]
          : [{ type: 'Features', id: 'LIST' }],
    }),

    deleteFeature: builder.mutation<{ success: boolean; message: string }, string>({
      queryFn: async (id) => {
        try {
          const data = await deleteFeature(id);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      invalidatesTags: [{ type: 'Features', id: 'LIST' }],
    }),

    // Feature Group endpoints
    getAllFeatureGroupsWithFeatures: builder.query<FeatureGroup[], string>({
      queryFn: async (organizationId) => {
        try {
          const data = await getAllFeatureGroupsWithFeatures(organizationId);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({
                type: 'FeatureGroups' as const,
                id: _id,
              })),
              { type: 'FeatureGroups', id: 'LIST' },
            ]
          : [{ type: 'FeatureGroups', id: 'LIST' }],
    }),

    getFeatureGroupById: builder.query<FeatureGroup, string>({
      queryFn: async (id) => {
        try {
          const data = await getFeatureGroupById(id);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      providesTags: (result) => (result ? [{ type: 'FeatureGroup', id: result._id }] : []),
    }),

    createFeatureGroup: builder.mutation<FeatureGroup, CreateFeatureGroupDTO>({
      queryFn: async (groupData) => {
        try {
          const data = await createFeatureGroup(groupData);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      invalidatesTags: [{ type: 'FeatureGroups', id: 'LIST' }],
    }),

    updateFeatureGroup: builder.mutation<
      FeatureGroup,
      { id: string; groupData: UpdateFeatureGroupDTO }
    >({
      queryFn: async ({ id, groupData }) => {
        try {
          const data = await updateFeatureGroup(id, groupData);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'FeatureGroups', id: 'LIST' },
              { type: 'FeatureGroup', id: result._id },
            ]
          : [{ type: 'FeatureGroups', id: 'LIST' }],
    }),

    deleteFeatureGroup: builder.mutation<{ success: boolean; message: string }, string>({
      queryFn: async (id) => {
        try {
          const data = await deleteFeatureGroup(id);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      invalidatesTags: [
        { type: 'FeatureGroups', id: 'LIST' },
        { type: 'Features', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetAllFeaturesQuery,
  useGetFeatureByIdQuery,
  useGetFeaturesByGroupIdQuery,
  useCreateFeatureMutation,
  useUpdateFeatureMutation,
  useDeleteFeatureMutation,
  useGetAllFeatureGroupsWithFeaturesQuery,
  useGetFeatureGroupByIdQuery,
  useCreateFeatureGroupMutation,
  useUpdateFeatureGroupMutation,
  useDeleteFeatureGroupMutation,
} = featuresApiSlice;
